import apiClient, { ApiResponse, PaginationMeta } from '../api/client';

/**
 * Client for the WhatsApp admin/ops endpoints. These live under /api/whatsapp/admin/*
 * (separate from the main admin API), Sanctum-protected, read-only diagnostics.
 */

export interface WhatsAppStats {
  sessions_total: number;
  sessions_active: number;
  messages_today_inbound: number;
  messages_today_outbound: number;
  messages_failed_today: number;
}

export interface WhatsAppMessage {
  id: number;
  wa_phone: string;
  direction: 'inbound' | 'outbound';
  touchpoint_id: number | null;
  wa_message_id: string | null;
  type: string | null;
  payload: any;
  status: string | null;
  error: string | null;
  created_at: string;
}

export interface WhatsAppSession {
  id: number;
  wa_phone: string;
  county_id: number | null;
  touchpoint_id: number | null;
  user_id: number | null;
  state: string;
  context: any;
  expires_at: string | null;
  last_prompt_wa_message_id: string | null;
  created_at: string;
  updated_at: string;
  county?: { id: number; name: string } | null;
  user?: { id: number; first_name?: string; last_name?: string; email?: string } | null;
}

class WhatsAppAdminService {
  private base = '/whatsapp/admin';

  async stats(): Promise<ApiResponse<WhatsAppStats>> {
    return apiClient.get(`${this.base}/stats`);
  }

  async messages(params: { phone?: string; direction?: string; per_page?: number; page?: number } = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.append(k, String(v)));
    const s = qs.toString();
    return apiClient.get<WhatsAppMessage[]>(`${this.base}/messages${s ? `?${s}` : ''}`);
  }

  async sessions(params: { phone?: string; active?: boolean; per_page?: number; page?: number } = {}): Promise<ApiResponse<WhatsAppSession[]> & { meta?: PaginationMeta }> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && qs.append(k, String(v)));
    const s = qs.toString();
    return apiClient.get(`${this.base}/sessions${s ? `?${s}` : ''}`);
  }

  async stuckSessions(minutes = 60): Promise<ApiResponse<WhatsAppSession[]>> {
    return apiClient.get(`${this.base}/sessions/stuck?minutes=${minutes}`);
  }

  async conversation(phone: string, limit = 100): Promise<ApiResponse<{ session: WhatsAppSession | null; messages: WhatsAppMessage[] }>> {
    return apiClient.get(`${this.base}/conversations/${encodeURIComponent(phone)}?limit=${limit}`);
  }
}

export const whatsAppService = new WhatsAppAdminService();
